/* /employees/:id — port of prototype pgProfile (tabs). */
import { useRef, type ReactNode } from "react";
import InviteButton from "../account/InviteButton";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAllEmployees, useAttendanceMonth, useEmployee, useLeaveBalance, useLeaves, usePayrollHistory, usePayslip, useSettings, useToday, useUploadFile } from "../../data/hooks";
import { ApiError } from "../../data/provider";
import type { Employee, PayrollHistoryItem } from "../../data/types";
import { daysTo, yearsBetween } from "../../domain/dates";
import { eosb, gosiOf, payslip, ZERO_ADJ, annualEntitlement, usedAnnualDemo } from "../../domain/payroll";
import { useI18n } from "../../i18n";
import { addDays } from "../../domain/dates";
import { fmtN, gDate, moneyText, periodLabel } from "../../lib/format";
import { useSession } from "../../app/session";
import { useOverlays } from "../../app/overlays";
import {
  Avatar, Bar, Button, Card, Chip, DualDate, EmptyState, ErrorBox, HBars, Icon, Kpi, Kv, Money, Skeleton, Tabs, errMsg, useToast,
} from "../../ui";
import DocTable, { type DocRow } from "./components/DocTable";
import MonthGrid from "./components/MonthGrid";
import { canWriteHr, REL_AR } from "./components/shared";
import { activeCode, curL } from "../../lib/country";

type Tab = "overview" | "personal" | "job" | "salary" | "docs" | "deps" | "leave" | "attendance" | "payslips" | "eosb" | "activity";
const TABS: Tab[] = ["overview", "personal", "job", "salary", "docs", "deps", "leave", "attendance", "payslips", "eosb", "activity"];

function PayslipRow({ e, h }: { e: Employee; h: PayrollHistoryItem }) {
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const q = usePayslip(e.id, h.id);
  const to = "/payslip/" + e.id + "/" + h.id;
  return (
    <tr className="click" onClick={() => nav(to)} data-testid={"payslip-" + h.id}>
      <td className="k1"><Link to={to} onClick={(ev) => ev.stopPropagation()}><b>{periodLabel(h.id, lang)}</b></Link></td>
      <td className="x" data-l={t("Pay date")}><DualDate date={h.payDate} /></td>
      <td className="end k3" data-l={t("Net pay")}>{q.data ? <Money value={q.data.net} /> : q.error ? "—" : <span className="skel" style={{ display: "inline-block", width: 90, height: 14 }} />}</td>
      <td className="k2"><Chip status={h.status} /></td>
      <td className="end hide-md"><Icon name="chevron-right" className="s" /></td>
    </tr>
  );
}

function UploadDocButton({ employee }: { employee: Employee }) {
  const { t, L, name } = useI18n();
  const toast = useToast();
  const up = useUploadFile();
  const ref = useRef<HTMLInputElement>(null);
  return (
    <>
      <Button variant="sm" icon="upload" loading={up.isPending} onClick={() => ref.current?.click()} data-testid="btn-upload-doc">{t("Upload")}</Button>
      <input ref={ref} type="file" hidden accept=".pdf,.png,.jpg,.jpeg,.webp,.heic" aria-label={t("Upload")} data-testid="upload-doc-input"
        onChange={async (ev) => {
          const f = ev.target.files?.[0];
          ev.target.value = "";
          if (!f) return;
          try { await up.mutateAsync({ file: f, name: f.name, folder: "documents" }); toast(L("Document uploaded to " + employee.nameEn + "'s file", "تم رفع الوثيقة إلى ملف " + name(employee))); }
          catch (e) { toast(errMsg(e), "bad"); }
        }} />
    </>
  );
}

export default function EmployeeProfile() {
  const i = useI18n();
  const { t, L, lang, isAr } = i;
  const { id: idParam } = useParams();
  const id = Number(idParam);
  const [sp, setSp] = useSearchParams();
  const nav = useNavigate();
  const { role } = useSession();
  const { openPrint } = useOverlays();
  const today = useToday();
  const hr = canWriteHr(role);
  const valid = Number.isInteger(id) && id > 0;

  const q = useEmployee(id, { enabled: valid });
  const settingsQ = useSettings();
  const histQ = usePayrollHistory();
  const balQ = useLeaveBalance(id, { enabled: valid });
  const leavesQ = useLeaves({ enabled: hr });
  const allQ = useAllEmployees();
  const period = today.slice(0, 7);
  const tabParam = sp.get("tab") as Tab | null;
  const tab: Tab = tabParam && TABS.includes(tabParam) && (tabParam !== "attendance" || hr) ? tabParam : "overview";
  const attQ = useAttendanceMonth(id, period, { enabled: valid && hr && tab === "attendance" });

  if (!valid || (q.error instanceof ApiError && q.error.status === 404)) {
    return (
      <div data-testid="profile-404">
        <Link className="back" to="/employees"><Icon name="chevron-left" className="s" /> {t("Employees")}</Link>
        <EmptyState title={L("Employee not found", "الموظف غير موجود")} desc={L("The employee may have been removed, or the link is wrong.", "ربما حُذف الموظف أو أن الرابط غير صحيح.")} action={<Button variant="sm" onClick={() => nav("/employees")}>{t("Employees")}</Button>} />
      </div>
    );
  }
  if (q.error) return <ErrorBox error={q.error} onRetry={() => q.refetch()} />;
  if (!q.data || !settingsQ.data) return <Skeleton />;

  const e = q.data;
  const s = settingsQ.data;
  const y = yearsBetween(e.joined, today);
  const gross = e.basic + e.housing + e.transport + e.other;
  const eo = eosb(e, today, "termination", undefined, activeCode());
  const eoRes = eosb(e, today, "resignation", undefined, activeCode());
  const ent = balQ.data?.entitlement ?? annualEntitlement(e, today, activeCode());
  const used = balQ.data?.used ?? usedAnnualDemo(e);
  const left = balQ.data?.remaining ?? Math.max(0, ent - used);
  const g = gosiOf(e, s.gosi, today, activeCode());
  const ps = payslip(e, ZERO_ADJ, [], s.gosi, today, period, activeCode());
  const managerId = e.dept === "fin" ? 101 : 106;
  const manager = allQ.data?.find((x) => x.id === managerId);
  const docs: DocRow[] = e.documents.map((d) => ({ e, kind: d.kind, number: d.number, expiry: d.expiry, daysLeft: daysTo(today, d.expiry) }));
  const docsDue = docs.filter((d) => d.daysLeft <= 90).length;
  const isSa = e.nationality === "SA";
  const mismatch = e.gosiWage > 0 && Math.abs(e.basic + e.housing - e.gosiWage) / e.gosiWage > 0.2;
  const setTab = (k: Tab) => { const n = new URLSearchParams(sp); if (k === "overview") n.delete("tab"); else n.set("tab", k); setSp(n, { replace: true }); };

  const tabItems: [Tab, ReactNode, (number | null)?][] = [
    ["overview", t("Overview")], ["personal", t("Personal")], ["job", t("Job & contract")], ["salary", t("Salary & allowances")],
    ["docs", t("Documents"), docsDue || null], ["deps", L("Dependants & insurance", "المرافقون والتأمين"), e.dependants.length || null],
    ["leave", t("Leave balance")], ...(hr ? [["attendance", t("Attendance")] as [Tab, ReactNode]] : []), ["payslips", t("Payslips")], ["eosb", t("EOSB accrual")], ["activity", t("Activity log")],
  ];

  const qiwaChip = e.qiwa.status === "Authenticated" ? <Chip tone="ok">{L("Authenticated", "موثق")}</Chip> : <Chip tone="warn">{L("Pending authentication", "بانتظار التوثيق")}</Chip>;
  const gosiSystem = !isSa ? L("Non-Saudi · hazards only", "غير سعودي · الأخطار فقط")
    : e.gosiRegime === "new" ? L("New law · registered after 3 Jul 2024 · annuity " + g.annRate + "%", "النظام الجديد · مسجل بعد 3 يوليو 2024 · المعاشات " + g.annRate + "%")
    : L("Before 3 Jul 2024 · annuity " + g.annRate + "%", "قبل 3 يوليو 2024 · المعاشات " + g.annRate + "%");

  let body: ReactNode = null;
  if (tab === "overview") body = (
    <>
      <div className="g g-4 mb">
        <Kpi testId="kpi-years" label={t("Years of service")} value={y.toFixed(1)} icon="award" tone="gold" />
        <Kpi testId="kpi-gross" label={t("Monthly gross")} value={fmtN(gross, 0)} unit={curL(L)} icon="wallet" />
        <Kpi testId="kpi-leave" label={t("Leave left")} value={String(left)} unit={t("days")} icon="plane" tone="info" foot={L("of " + ent + " annual", "من " + ent + " سنوية")} />
        <Kpi testId="kpi-eosb" label={t("EOSB accrued")} value={fmtN(eo.award, 0)} unit={curL(L)} icon="receipt" tone="warn" />
      </div>
      <div className="g g-2">
        <Card title={t("Personal")}><Kv items={[[t("Mobile"), <span className="num" dir="ltr">{e.mobile}</span>], [t("Email"), e.email || "—"], [isSa ? t("National ID") : t("Iqama"), <span className="num">{e.idNumber}</span>], [t("Nationality"), i.nat(e.nationality)]]} /></Card>
        <Card title={t("Job & contract")}><Kv items={[[t("Job title"), i.title(e.title)], [t("Department"), i.dept(e.dept)], [t("Reports to"), manager && manager.id !== e.id ? <Link to={"/employees/" + manager.id}>{i.name(manager)}</Link> : "—"], [t("Joined"), <DualDate date={e.joined} />]]} /></Card>
      </div>
    </>
  );
  if (tab === "personal") body = (
    <div className="g g-2">
      <Card title={t("Personal")}><Kv items={[[L("Name (English)", "الاسم (إنجليزي)"), e.nameEn], [L("Name (Arabic)", "الاسم (عربي)"), <span dir="rtl">{e.nameAr}</span>], [t("Nationality"), i.nat(e.nationality)], [t("Gender"), t(e.gender === "F" ? "Female" : "Male")], [t("Date of birth"), e.dob ? <DualDate date={e.dob} /> : "—"], [L("Disability", "الإعاقة"), e.disabled ? L("Registered (counts ×4 in Nitaqat)", "مسجلة (تُحتسب ×4 في نطاقات)") : L("None", "لا يوجد")]]} /></Card>
      <Card title={L("Contact & bank", "التواصل والبنك")}><Kv items={[[t("Mobile"), <span className="num" dir="ltr">{e.mobile}</span>], [t("Email"), e.email || "—"], [L("Address", "العنوان"), L(e.branch === "jed" ? "Al Rawdah, Jeddah" : "Al Malqa, Riyadh", e.branch === "jed" ? "الروضة، جدة" : "الملقا، الرياض")], [t("Bank"), e.bank], [t("IBAN"), <span className="num" dir="ltr">{e.iban}</span>]]} /></Card>
    </div>
  );
  if (tab === "job") body = (
    <div className="g g-2">
      <Card title={L("Position", "المنصب")}><Kv items={[[t("Job title"), i.title(e.title)], [t("Department"), i.dept(e.dept)], [t("Branch"), i.branch(e.branch)], [L("Grade", "الدرجة"), "G" + (4 + (e.basic % 7))], [L("Work week", "أيام العمل"), s.attendance.weekendDays.length === 1 ? L("6-day week · Friday off", "6 أيام · العطلة الجمعة") : L("Sun–Thu · Fri–Sat weekend", "الأحد–الخميس · العطلة الجمعة والسبت")], [L("Daily hours", "ساعات العمل"), s.attendance.shiftStart + "–" + s.attendance.shiftEnd + " · " + L("6 in Ramadan", "6 في رمضان")], [L("Probation ends", "نهاية التجربة"), e.probationEnd ? <DualDate date={e.probationEnd} /> : "—"]]} /></Card>
      <Card title={t("Contract")} actions={qiwaChip}><Kv items={[[t("Contract type"), isSa ? t("Unlimited") : t("Fixed term") + L(" · 2 years", " · سنتان")], [L("Qiwa contract no.", "رقم عقد قوى"), <span className="num">{e.qiwa.contractNo || "—"}</span>], [L("Start", "البداية"), <DualDate date={e.joined} />], [L("Probation", "فترة التجربة"), L("90 days (extendable to 180)", "90 يومًا (قابلة للتمديد إلى 180)")], [L("Notice period", "فترة الإشعار"), L("60 days", "60 يومًا")], [L("Qiwa status", "حالة قوى"), qiwaChip], [L("Air ticket", "تذكرة السفر"), e.ticket ? L(e.ticket.cls + " · " + e.ticket.dest + " · yearly", e.ticket.dest + " · سنويًا") : "—"]]} /></Card>
    </div>
  );
  if (tab === "salary") body = (
    <div className="g g-2-1">
      <Card title={L("Monthly salary", "الراتب الشهري")} sub={L("Effective 01 Jan " + today.slice(0, 4), "اعتبارًا من 01 يناير " + today.slice(0, 4))} flush testId="salary-card">
        <div className="tw">
          <table className="tbl">
            <tbody>
              {([[t("Basic salary"), e.basic], [t("Housing") + " (" + s.allowances.housing + "%)", e.housing], [t("Transport") + " (" + s.allowances.transport + "%)", e.transport], [t("Other"), e.other]] as [string, number][]).map(([l, v]) => <tr key={l}><td>{l}</td><td className="end"><Money value={v} /></td></tr>)}
            </tbody>
            <tfoot><tr><td>{t("Gross")}</td><td className="end"><Money value={gross} /></td></tr></tfoot>
          </table>
        </div>
      </Card>
      <Card title={t("GOSI")} sub={isSa ? L("Annuity + SANED + occupational hazards", "المعاشات + ساند + الأخطار المهنية") : L("Occupational hazards only (employer)", "الأخطار المهنية فقط (صاحب العمل)")} testId="gosi-card">
        <Kv items={[
          [L("GOSI system", "نظام التأمينات"), gosiSystem],
          [L("GOSI-registered wage", "الأجر المسجل في التأمينات"), <><Money value={e.gosiWage} />{mismatch && <> <Chip tone="bad" testId="mudad-mismatch">{L("Mudad mismatch", "اختلاف مدد")}</Chip></>}</>],
          [L("Contribution base", "أجر الاشتراك"), <Money value={g.base} />],
          [t("Employee share"), <Money value={g.emp} />],
          [t("Employer share"), <Money value={g.er} />],
          [t("Net pay"), <b><Money value={ps.net} /></b>],
          [t("Bank"), e.bank],
          [t("IBAN"), <span className="num" dir="ltr">{e.iban}</span>],
        ]} />
      </Card>
    </div>
  );
  if (tab === "docs") body = (
    <Card title={t("Documents")} flush actions={hr ? <UploadDocButton employee={e} /> : undefined} testId="docs-card">
      {docs.length ? <DocTable rows={docs} withEmp={false} today={today} canRenew={hr} testId="profile-docs" /> : <EmptyState title={L("No documents on file", "لا توجد وثائق")} desc={L("Upload the ID, passport and insurance card.", "ارفع الهوية والجواز وبطاقة التأمين.")} />}
    </Card>
  );
  if (tab === "deps") body = (
    <div className="g g-2-1">
      <Card title={L("Dependants", "المرافقون")} flush testId="deps-card">
        {e.dependants.length ? (
          <div className="tw">
            <table className="tbl">
              <thead><tr><th>{L("Relation", "الصلة")}</th><th>{L("Insurance", "التأمين")}</th><th className="end">{!isSa ? L("Fee / month", "الرسوم / شهر") : ""}</th></tr></thead>
              <tbody>{e.dependants.map((d, k) => <tr key={k}><td>{L(d.relation, REL_AR[d.relation] ?? d.relation)}</td><td>{d.insurance}</td><td className="end">{!isSa ? <Money value={400} dec={0} /> : "—"}</td></tr>)}</tbody>
            </table>
          </div>
        ) : <EmptyState title={L("No dependants", "لا يوجد مرافقون")} desc={L("Add family members to manage their insurance and fees.", "أضف أفراد الأسرة لإدارة تأمينهم ورسومهم.")} />}
      </Card>
      <Card title={L("Medical insurance (CCHI)", "التأمين الطبي")}>
        <Kv items={[[L("Class", "الفئة"), e.cchiClass || "—"], [L("Members", "الأعضاء"), String(1 + e.dependants.length)], [L("Policy renews", "تجديد الوثيقة"), (() => { const m = e.documents.find((d) => d.kind === "Medical insurance"); return m ? <DualDate date={m.expiry} /> : "—"; })()]]} />
      </Card>
    </div>
  );
  if (tab === "leave") {
    const bal: [string, number, number, string][] = [["Annual", ent, used, ""], ["Sick", 30, e.id % 4, "info"], ["Hajj", 10, 0, "gold"], ["Exam", 5, 0, ""]];
    const mine = (leavesQ.data ?? []).filter((l) => l.employeeId === e.id);
    body = (
      <>
        <div className="g g-4 mb" data-testid="leave-balances">
          {bal.map(([k, tot, u, tone]) => (
            <div key={k} className="card kpi">
              <div className="kpi-top">{t(k)}</div>
              <div className="kpi-v">{Math.max(0, tot - u)}<span className="kpi-u">{L("days left", "يوم متبقٍ")}</span></div>
              <Bar value={u} max={tot} tone={tone} />
              <div className="kpi-f">{u} {L("used of", "مستخدم من")} {tot}</div>
            </div>
          ))}
        </div>
        {hr && (
          <Card title={L("History", "السجل")} flush testId="leave-history">
            {mine.length ? (
              <div className="tw">
                <table className="tbl cards">
                  <thead><tr><th>{t("Type")}</th><th className="hide-md">{t("From")}</th><th className="hide-md">{t("To")}</th><th className="end">{t("Days")}</th><th>{t("Status")}</th></tr></thead>
                  <tbody>{mine.map((l) => <tr key={l.id}><td className="k1">{t(l.type)}</td><td className="hide-md x" data-l={t("From")}><DualDate date={l.from} /></td><td className="hide-md x" data-l={t("To")}><DualDate date={l.to} /></td><td className="end x" data-l={t("Days")}><span className="num">{l.days}</span></td><td className="k2"><Chip status={l.status} /></td></tr>)}</tbody>
                </table>
              </div>
            ) : <EmptyState title={L("No leave requests", "لا توجد طلبات إجازة")} desc={L("New requests will appear here.", "ستظهر الطلبات الجديدة هنا.")} />}
          </Card>
        )}
      </>
    );
  }
  if (tab === "attendance") body = (
    <Card title={periodLabel(period, lang)} sub={s.attendance.weekendDays.map((d) => L(["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][d], ["الأحد", "الإثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"][d])).join(L(" & ", " و")) + " " + L("weekend", "عطلة")} testId="attendance-card">
      {attQ.error ? <ErrorBox error={attQ.error} onRetry={() => attQ.refetch()} /> : !attQ.data ? <Skeleton /> : <MonthGrid period={period} rows={attQ.data} today={today} weekendDays={s.attendance.weekendDays} />}
    </Card>
  );
  if (tab === "payslips") {
    const months = (histQ.data ?? []).slice(-6).reverse();
    body = (
      <Card title={t("Payslips")} flush testId="payslips-card">
        {months.length ? (
          <div className="tw">
            <table className="tbl cards">
              <thead><tr><th>{t("Month")}</th><th>{t("Pay date")}</th><th className="end">{t("Net pay")}</th><th>{t("Status")}</th><th /></tr></thead>
              <tbody>{months.map((h) => <PayslipRow key={h.id} e={e} h={h} />)}</tbody>
            </table>
          </div>
        ) : histQ.isLoading ? <Skeleton /> : <EmptyState title={L("No payslips yet", "لا توجد قسائم بعد")} />}
      </Card>
    );
  }
  if (tab === "eosb") {
    const years = [1, 2, 3, 4, 5, 6, 8, 10].filter((k) => k <= Math.ceil(eo.years) + 1);
    body = (
      <div className="g g-2-1">
        <Card title={L("Live accrual", "الاستحقاق الحالي")} testId="eosb-card">
          <div className="kpi-v" style={{ fontSize: 34 }}><Money value={eo.award} /></div>
          <p className="muted sm">{L("If employment ended today by the employer, after " + eo.years.toFixed(2) + " years of service.", "لو انتهت الخدمة اليوم من قبل صاحب العمل بعد " + eo.years.toFixed(2) + " سنة.")}</p>
          <Kv items={[[L("Wage used", "الأجر المعتمد"), <Money value={eo.wage} />], [L("First 5 years × ½ month", "أول 5 سنوات × نصف شهر"), <Money value={eo.first} />], [L("After 5 years × 1 month", "بعد 5 سنوات × شهر"), <Money value={eo.rest} />], [L("If resigning today", "عند الاستقالة اليوم"), <Money value={eoRes.award} />]]} />
          {hr && <div className="mt"><Button variant="sm" icon="receipt" onClick={() => nav("/lifecycle")}>{L("Open EOSB calculator", "فتح حاسبة نهاية الخدمة")}</Button></div>}
        </Card>
        <Card title={L("Accrual by year", "الاستحقاق حسب السنة")}>
          <HBars data={years.map((k) => [L("Year " + k, "السنة " + k), Math.round(eosb(e, addDays(e.joined, Math.round(k * 365.2425)), "termination", undefined, activeCode()).award)] as [string, number])} />
        </Card>
      </div>
    );
  }
  if (tab === "activity") {
    const acts: [string, string, string, string][] = [
      ["edit", L("Salary updated to ", "تم تحديث الراتب إلى ") + moneyText(e.basic, lang), "Sara Al-Otaibi", today.slice(0, 4) + "-01-01"],
      ...(e.iqamaExpiry ? [["id-card", L("Iqama renewed via Muqeem", "تم تجديد الإقامة عبر مقيم"), "Abdulrahman Al-Juhani", addDays(e.iqamaExpiry, -365)] as [string, string, string, string]] : []),
      ["file", e.qiwa.status === "Authenticated" ? L("Contract authenticated on Qiwa", "تم توثيق العقد في قوى") : L("Contract sent to Qiwa for authentication", "أُرسل العقد إلى قوى للتوثيق"), L("System", "النظام"), e.joined],
      ["user-plus", L("Employee file created", "تم إنشاء ملف الموظف"), L("System", "النظام"), e.joined],
    ];
    body = (
      <Card title={t("Activity log")} testId="activity-card">
        {acts.sort((a, b) => (a[3] < b[3] ? 1 : -1)).map((a, k) => (
          <div key={k} className="list-row"><span className="kpi-ico"><Icon name={a[0]} /></span><div className="grow"><div>{a[1]}</div><div className="xs muted">{a[2]} · {gDate(a[3], lang)}</div></div></div>
        ))}
      </Card>
    );
  }

  return (
    <div data-testid="employee-profile">
      <Link className="back" to="/employees"><Icon name="chevron-left" className="s" /> {t("Employees")}</Link>
      <section className="card prof" data-testid="profile-head">
        <Avatar name={e.nameEn} size="lg" />
        <div className="grow" style={{ minWidth: 220 }}>
          <h1>{i.name(e)}<span className="alt" dir={isAr ? "ltr" : "rtl"}>{i.name2(e)}</span></h1>
          <div className="row sm muted" style={{ margin: "4px 0 8px" }}>{i.title(e.title)} · {i.dept(e.dept)} · {i.branch(e.branch)} · <span className="num">{e.code}</span></div>
          <div className="row" style={{ gap: 6 }}>
            <Chip status={e.status} />
            {isSa ? <Chip tone="pri" nodot>{t("Saudi")}</Chip> : <Chip nodot>{i.nat(e.nationality)}</Chip>}
            <Chip tone="gold" nodot>{y.toFixed(1)} {t("years")}</Chip>
          </div>
        </div>
        <div className="prof-actions">
          <Button icon="printer" onClick={() => openPrint({ doc: "idcard", emp: e.id })} data-testid="btn-print">{t("Print")}</Button>
          {hr && <Button icon="mail" onClick={() => nav("/letters")} data-testid="btn-letters">{t("Letters & requests")}</Button>}
          {hr && <Button icon="edit" onClick={() => nav("/employees/" + e.id + "/edit")} data-testid="btn-edit">{t("Edit")}</Button>}
          {hr && <InviteButton employee={e} />}
        </div>
      </section>
      <div className="mt"><Tabs<Tab> items={tabItems} value={tab} onChange={setTab} label={L("Employee sections", "أقسام ملف الموظف")} /></div>
      <div data-testid={"tab-body-" + tab}>{body}</div>
    </div>
  );
}
